import React, { useState, useEffect } from "react";
import {
  Typography,
  Select,
  Button,
  List,
  Spin,
  Row,
  Col,
  message,
  Card,
  Modal,
  Drawer,
  Divider,
  Tooltip
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router-dom";
import poiApi from "../../services/poiApi";
import tripManagementApi from '../../services/tripManagementApi';
import routApi from "../../services/routeApi";
import trippoiApi from "../../services/tripPoi";
import "./rearrangLlist.css"

const { Title, Paragraph } = Typography;
const { Option } = Select;

// Sortable POI item
const SortableItem = ({ id, name }: { id: string; name: string }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    padding: "12px 16px",
    marginBottom: "8px",
    background: "#f9f9f9",
    borderRadius: 8,
    cursor: "grab",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {name}
    </div>
  );
};

const TripPOIRearrangePage: React.FC = () => {
  const [directionId, setDirectionId] = useState<string | null>(null);
  const [trips, setTrips] = useState<any[]>([]); // Trip list
  const [tripId, setTripId] = useState<string | null>(null);
  const [routes, setRoutes] = useState<any[]>([]); // Routes list
  const [routeId, setRouteId] = useState<string | null>(null);
  const [pois, setPois] = useState<any[]>([]);
  const [originalPois, setOriginalPois] = useState<any[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingPois, setLoadingPois] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [routeName, setRouteName] = useState("");
  const [routeDescription, setRouteDescription] = useState("");
  const [routeFileUrl, setRouteFileUrl] = useState("");
  const DEFAULT_IMAGE = "/images/TripPOI-04.png";
  const [isTripDisabled, setIsTripDisabled] = useState(true);
  const [isRouteDisabled, setIsRouteDisabled] = useState(true);
  const isReordered = () => doneClicked && changesMade;
  // Load trips when direction changes
  useEffect(() => {
    if (!directionId) {
      setTrips([]);
      setTripId(null);
      setRoutes([]);
      setRouteId(null);
      setPois([]);
      setIsTripDisabled(true);
      setIsRouteDisabled(true);
      return;
    }

    const fetchTrips = async () => {
      setLoadingTrips(true);
      setTripId(null);
      setRoutes([]);
      setRouteId(null);
      setPois([]);
      setIsTripDisabled(true);
      setIsRouteDisabled(true);

      try {
        const res = await tripManagementApi.getTripManagementByDirectionId(directionId);
        if (!res || res.length === 0) {
          messageApi.warning("No Trips found for the selected direction.");
        } else {
          setTrips(res);
          setIsTripDisabled(false); // ✅ enable trip select once trips loaded
        }
      } catch {
        message.error("Failed to load trips");
      } finally {
        setLoadingTrips(false);
      }
    };

    fetchTrips();
  }, [directionId]);


  // Load routes when trip changes
  // When direction changes → clear trip, route, POI
  useEffect(() => {
    if (!directionId || directionId === "-999") {
      // Clear all dependent data
      setTrips([]);
      setTripId(null);
      setRoutes([]);
      setRouteId(null);
      setPois([]);
      return;
    }

    const fetchTrips = async () => {
      setLoadingTrips(true);
      setTripId(null);
      setRoutes([]);
      setRouteId(null);
      setPois([]);
      try {
        const res = await tripManagementApi.getTripManagementByDirectionId(directionId);
        if (!res || res.length === 0) {
          messageApi.warning("No Trips found for the selected direction.");
          setTrips([]);
        } else {
          setTrips(res);
        }
      } catch {
        messageApi.error("Failed to load trips");
        setTrips([]);
      } finally {
        setLoadingTrips(false);
      }
    };

    fetchTrips();
  }, [directionId]);

  // When trip changes → load routes
  useEffect(() => {
    if (!tripId) {
      setRoutes([]);
      setRouteId(null);
      setPois([]);
      return;
    }

    const fetchRoutes = async () => {
      setLoadingRoutes(true);
      setRouteId(null);
      setPois([]);
      try {
        const res = await routApi.getRouteByTripId(tripId);
        if (!res || res.length === 0) {
          messageApi.warning("No route found for the selected trip.");
          setRoutes([]);
        } else {
          setRoutes(res);
        }
      } catch (err) {
        messageApi.error("Failed to load routes");
        setRoutes([]);
      } finally {
        setLoadingRoutes(false);
      }
    };

    fetchRoutes();
  }, [tripId]);

  // When route changes → load POIs
  useEffect(() => {
    if (!routeId) {
      setPois([]);
      setOriginalPois([]);
      setRouteName("");
      setRouteDescription("");
      setRouteFileUrl("");
      return;
    }

    const fetchPois = async () => {
      setLoadingPois(true);
      try {
        setPois([]);
        setOriginalPois([]);

        const res = await trippoiApi.getTripPOIByRouteId(routeId);
        const tripPoiArray = Array.isArray(res) ? res : [res];

        if (tripPoiArray.length === 0) {
          messageApi.warning("No Trip POIs found for the selected route.");
          setPois([]);
          setOriginalPois([]);
        } else {
          setPois(tripPoiArray);
          setOriginalPois(tripPoiArray);
        }

        // Get route details
        const selectedRoute = routes.find((r) => r.id === routeId);
        setRouteName(selectedRoute?.name || "");
        setRouteDescription(selectedRoute?.description || "");
        setRouteFileUrl(selectedRoute?.fileUrl || "");
      } catch {
        messageApi.error("Failed to load POIs");
        setPois([]);
        setOriginalPois([]);
      } finally {
        setLoadingPois(false);
      }
    };

    fetchPois();
  }, [routeId, routes]);


  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = pois.findIndex((p) => p.id === active.id);
    const newIndex = pois.findIndex((p) => p.id === over.id);

    const newPois = arrayMove(pois, oldIndex, newIndex);
    setPois(newPois);

    // Mark as changed
    if (!changesMade) setChangesMade(true);
  };

  const validateBeforeSave = (): boolean => {
    if (!directionId || directionId === "-999") {
      messageApi.error("Please select a direction");
      return false;
    }

    if (!tripId) {
      messageApi.error("Please select a trip");
      return false;
    }

    if (!routeId) {
      messageApi.error("Please select a route");
      return false;
    }

    if (!pois || pois.length === 0) {
      messageApi.warning("No POIs found for this route");
    }

    return true;
  };
  const handleSave = async () => {
    if (!routeId) {
      messageApi.error("Route ID is required to save POIs");
      return;
    }

    if (pois.length === 0) {
      messageApi.warning("No POIs to save for this route");
      return;
    }

    const payload = {
      routeID: routeId,
      sortTripPOIsRequests: pois.map((poi, index) => ({
        routePOIsID: poi.id,  // Correct key for API
        sortOrder: index,
      })),
    };

    try {
      await trippoiApi.saveReorderTripPOIs(payload); // replace with your actual API call
      messageApi.success("Trip POIs rearranged successfully");
      setOriginalPois([...pois]); // Update original order after successful save
    } catch (err) {
      console.error(err);
      messageApi.error("Failed to reorder Trip POIs");
    }
  };


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const [doneClicked, setDoneClicked] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const showModal = () => {

    if (!pois || pois.length === 0) {
      messageApi.warning("No Trip POIs found for the selected route");
      return false; // if you want to prevent saving
    }
    setIsModalOpen(true);
  };
  const handleOk = () => {
    messageApi.info("Changes discarded.");
    setIsModalOpen(false);
    setPois([...originalPois]);    // reset POIs order
    setIsEditMode(false);          // exit edit mode
    setDoneClicked(false);         // reset Done state
    setChangesMade(false);         // reset changes state
  };
  const handleCancel = () => setIsModalOpen(false);

  const showsaveModal = () => {
    if (validateBeforeSave()) {
      setIsSaveModalOpen(true);
    }
  };
  const handleSaveOk = () => {
    handleSave();                  // Save API call
    setIsSaveModalOpen(false);     // close modal
    setIsEditMode(false);          // exit edit mode
    setDoneClicked(false);         // reset Done state
    setChangesMade(false);         // reset changes state
  };
  const handleSaveCancel = () => setIsSaveModalOpen(false);



  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Row gutter={[16, 16]}>
        {/* Left column */}
        <Col xs={24} lg={16}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Title level={3} style={{ color: "#41787C", marginBottom: 0 }}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined style={{ fontSize: 18, color: "#41787C", marginRight: 10 }} />}
                onClick={() => navigate(-1)}
              />
              Rearrange Trip POI
            </Title>
            <Button
              icon={isEditMode ? <CheckOutlined /> : <EditOutlined />}
              onClick={() => {
                if (isEditMode) {
                  setDoneClicked(true);  // Done clicked
                  setIsEditMode(false);  // exit edit mode
                } else {
                  setIsEditMode(true);
                  setDoneClicked(false); // reset if re-entering edit
                  setChangesMade(false); // reset changes
                }
              }}
              disabled={pois.length === 0}
            >
              {isEditMode ? "Done" : "Edit"}
            </Button>
          </div>

          {/* Direction select */}
          <div style={{ marginBottom: 16 }}>
            <Title level={5}>Direction<span style={{ color: "red" }}>*</span></Title>
            <Select
              value={directionId || "-999"}
              style={{ width: "100%" }}
              onChange={(value) => {
                setDirectionId(value);

                // Clear both trip & route dropdowns
                setTripId(null);
                setRouteId(null);
                setTrips([]);
                setRoutes([]);
                setPois([]);

                // Disable both dropdowns until data is fetched
                setIsTripDisabled(true);
                setIsRouteDisabled(true);
              }}
            >
              <Option value="-999">Select Direction</Option>
              <Option value="0">Northwest</Option>
              <Option value="1">Southeast</Option>
            </Select>
          </div>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {/* Trip Select */}
            <Col xs={24} md={12}>
              <Title level={5}>Trip<span style={{ color: "red" }}>*</span></Title>
              <Select
                value={tripId || ""}
                onChange={(val) => setTripId(val || null)}
                style={{ width: "100%" }}
                placeholder="Select a trip"
                disabled={!directionId || directionId === "-999"}
              >
                <Option value="">Select a trip</Option>
                {trips.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.tripName}
                  </Option>
                ))}
              </Select>
              {loadingTrips && <Spin size="small" />}
            </Col>

            {/* Route Select */}
            <Col xs={24} md={12}>
              <Title level={5}>Route<span style={{ color: "red" }}>*</span></Title>
              <Select
                value={routeId || ""}
                onChange={(val) => setRouteId(val || null)}
                style={{ width: "100%" }}
                placeholder="Select a route"
                disabled={!tripId}
              >
                <Option value="">Select a route</Option>
                {routes.map((r) => (
                  <Option key={r.id} value={r.id}>
                    {r.name}
                  </Option>
                ))}
              </Select>
              {loadingRoutes && <Spin size="small" />}
            </Col>
          </Row>

          {/* POIs List */}
          <div>
            <Title level={5}>Trip POIs</Title>
            {loadingPois ? (
              <Spin />
            ) : !routeId ? (
              <Paragraph>Select a route to view POIs.</Paragraph>
            ) : pois.length === 0 ? (
              <Paragraph>No POIs found for the selected route.</Paragraph>
            ) : isEditMode ? (
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={pois.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                  {pois.map((poi) => (
                    <SortableItem key={poi.id} id={poi.id} name={poi.mile + " - " + poi.location} />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              <List
                dataSource={pois}
                renderItem={(poi) => (
                  <Card
                    key={poi.id}
                    style={{
                      marginBottom: 12,
                      borderRadius: 8,
                      background: "#f9f9f9",
                    }}
                  >
                    {/* <Title level={5} style={{ marginBottom: 8 }}>
                      Mile: {poi.mile}
                    </Title> */}
                    <Paragraph style={{ marginBottom: 4, fontWeight: 500 }}>
                      {/* Location:  */}
                      {poi.location}
                    </Paragraph>
                  </Card>
                )}
              />
            )}
          </div>
        </Col>

        {/* Right Info Panel */}
        <Col xs={24} lg={8}>
          <Card style={{ textAlign: "center", borderRadius: 12 }}>
            <img src={DEFAULT_IMAGE} alt="route" style={{ width: "100%", height: 400, borderRadius: 8, marginBottom: 16, objectFit: "cover" }} />
            <Title level={5}>{routeName}</Title>
            <p style={{ color: "#555" }}>{routeDescription}</p>
            <div
              style={{
                marginTop: 30,
                display: "flex",
                justifyContent: "center", // centers the whole row
                alignItems: "center",
                gap: 16, // equal space between buttons
                flexWrap: "nowrap",
              }}
              className="responsive-buttons"
            >
              <Button onClick={() => setPreviewVisible(true)} disabled={pois.length === 0}>Mobile Preview</Button>
              <Button danger onClick={showModal} disabled={!isReordered()}>Discard</Button>

              <Modal
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={null} // custom footer
                centered
                closable={false}
                bodyStyle={{
                  textAlign: "center",
                  padding: "24px 16px",
                  borderRadius: 12,
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  Discard Changes
                </h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                  Are you sure you want to discard changes?
                </p>

                {/* Custom Buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Button
                    type="primary"
                    block
                    style={{
                      backgroundColor: "#41787C",
                      borderColor: "#41787C",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleOk}
                  >
                    Yes
                  </Button>

                  <Button
                    block
                    style={{
                      backgroundColor: "#f2f2f2",
                      border: "none",
                      color: "#333",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Modal>

              <Button type="primary" onClick={showsaveModal} disabled={!isReordered()}>Save</Button>

              <Modal
                open={isSaveModalOpen}
                onOk={handleSaveOk}
                onCancel={handleSaveCancel}
                footer={null}
                closable={false}   // hides the default X close button
                mask={false}       // disables dark overlay
                maskClosable={false} // clicking outside does NOT close modal
                centered
                bodyStyle={{
                  textAlign: "center",
                  padding: "24px 16px",
                  borderRadius: 12,
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                  Save Changes
                </h3>
                <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
                  Are you sure you want to save the changes?
                </p>

                {/* Custom Buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Button
                    type="primary"
                    block
                    style={{
                      backgroundColor: "#41787C",
                      borderColor: "#41787C",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleSaveOk} // call your save function here
                  >
                    Yes, Save
                  </Button>

                  <Button
                    block
                    style={{
                      backgroundColor: "#f2f2f2",
                      border: "none",
                      color: "#333",
                      height: 40,
                      fontWeight: 500,
                    }}
                    onClick={handleSaveCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Modal>


              <Drawer
                open={previewVisible}
                onClose={() => setPreviewVisible(false)}
                placement="right"
                width={420}
                closable={false}
                bodyStyle={{ padding: 0, display: "flex", justifyContent: "center" }}
              >
                <div
                  style={{
                    width: 360,
                    height: 640,
                    border: "16px solid black",
                    borderRadius: "30px",
                    padding: "0",
                    position: "relative",
                    background: "#f9f9f9",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 12,
                      borderBottom: "1px solid #ddd",
                      fontWeight: "bold",
                      fontSize: 16,
                      color: "#a0522d",
                    }}
                  >
                    <ArrowLeftOutlined
                      style={{ fontSize: 18, cursor: "pointer" }}
                      onClick={() => setPreviewVisible(false)}
                    />
                    <div style={{ flex: 1, textAlign: "center", marginLeft: -18 }}>
                      Trip POIs
                    </div>
                    <div style={{ width: 18 }} />
                  </div>

                  {/* Scrollable content */}
                  <div style={{ flex: 1, overflowY: "auto" }}>
                    <div style={{ background: "#fff", padding: 12 }}>
                      {/* Route Info */}
                      <h3 style={{ marginBottom: "-16px" }}>{routeName}</h3>
                      <Divider style={{ borderColor: "#a0522d", borderWidth: 1 }} />
                      {/* Map placeholder */}
                      <div
                        style={{
                          height: 160,
                          borderRadius: 8,
                          overflow: "hidden",
                          marginBottom: 12,
                          background: "#eee",
                        }}
                      >
                        <img
                          src={"/images/ra-mapbox.jpg"}
                          alt="map"
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                      {/* Map / Image */}
                      {routeFileUrl && (
                        <div
                          style={{
                            height: 160,
                            borderRadius: 8,
                            overflow: "hidden",
                            marginBottom: 12,
                            background: "#eee",
                          }}
                        >
                          <img
                            src={routeFileUrl}
                            alt="route"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                      )}

                      {/* POI List */}
                      {/* POI List */}
                      {pois.map((poi) => (
                        <div
                          key={poi.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {/* Column 1: Image */}
                          <div style={{ width: 50, flexShrink: 0 }}>
                            <img
                              src={
                                poi.routePOIsImageResponse?.length > 0
                                  ? poi.routePOIsImageResponse[0].fileUrl
                                  : "/icons/storeIcon.svg"
                              }
                              alt={
                                poi.routePOIsImageResponse?.length > 0
                                  ? "Trip PI"
                                  : "default image"
                              }
                              style={{
                                width: 35,
                                height: 35,
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          </div>

                          {/* Column 2: Location */}
                          <div
                            style={{
                              flex: 1,
                              fontSize: 12,
                              color: "#888",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              marginLeft: 8,
                            }}
                          >
                            <Tooltip title={poi.location}>{poi.location}</Tooltip>
                          </div>

                          {/* Column 3: Miles */}
                          <div
                            style={{
                              width: 60,
                              fontSize: 12,
                              color: "#888",
                              textAlign: "right",
                              flexShrink: 0,
                            }}
                          >
                            {poi.mile || "0"} mile
                          </div>

                          {/* Column 4: View More */}
                          <div
                            style={{
                              width: 70,
                              textAlign: "right",
                              flexShrink: 0,
                            }}
                          >
                            <a style={{ fontSize: 12, color: "#1890ff" }}>View More</a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer button */}
                  <Button
                    block
                    type="primary"
                    style={{ borderRadius: 0 }}
                    onClick={() => setPreviewVisible(false)}
                  >
                    Close Preview
                  </Button>
                </div>
              </Drawer>


            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TripPOIRearrangePage;

